/**
 * UX initiative IDs for revenue / conversion attribution (issue #71).
 * Pass `ux_initiative_id` on `trackFunnelEvent` payloads; BI tools filter on this field.
 */

export const UX_INITIATIVE_IDS = {
  PAYMENT_METHOD_OPTIMIZATION: 'UX-65-payment-method',
  CART_CONFIDENCE: 'UX-70-cart-confidence',
} as const;

export type UxInitiativeId = (typeof UX_INITIATIVE_IDS)[keyof typeof UX_INITIATIVE_IDS];

export type UxInitiativeRegistryEntry = {
  id: UxInitiativeId;
  issue: string;
  title: string;
  /** Primary `event` names used to measure this initiative */
  primaryFunnelEvents: readonly string[];
  /** How to infer revenue / AOV / conversion (see docs/analytics/ux-revenue-attribution.md) */
  revenueSignals: readonly string[];
  attributionNotes: string;
};

/**
 * Registry: map initiatives → events + measurement intent.
 * Keep in sync with `docs/analytics/ux-revenue-attribution.md`.
 */
export const UX_INITIATIVE_REGISTRY: readonly UxInitiativeRegistryEntry[] = [
  {
    id: UX_INITIATIVE_IDS.PAYMENT_METHOD_OPTIMIZATION,
    issue: '#65',
    title: 'Payment method optimization by segment',
    primaryFunnelEvents: [
      'funnel_order_success',
      'funnel_order_failure',
      'funnel_checkout_validation_failure',
    ],
    revenueSignals: [
      'Conversion: funnel_order_success with ux_initiative_id (step 3 payment path)',
      'Payment friction: validation_failure error_type=payment + preauth / intake failures tagged',
      'AOV: cart_value_bucket on same events',
    ],
    attributionNotes:
      'Last-touch style on payment step outcomes. Does not claim exclusive credit for all checkouts.',
  },
  {
    id: UX_INITIATIVE_IDS.CART_CONFIDENCE,
    issue: '#70',
    title: 'Cart confidence meter and objection handling',
    primaryFunnelEvents: ['funnel_ux_initiative_exposed', 'funnel_checkout_start'],
    revenueSignals: [
      'Exposure: funnel_ux_initiative_exposed ux_surface=cart_drawer_footer',
      'Conversion proxy: funnel_checkout_start source=cart_drawer_cta with ux_initiative_id (same session)',
    ],
    attributionNotes:
      'Correlational: compare checkout_start rate or order_success among exposed sessions vs holdout (feature flag or time period).',
  },
] as const;

export function tagPaymentMethodUx(): { ux_initiative_id: UxInitiativeId } {
  return { ux_initiative_id: UX_INITIATIVE_IDS.PAYMENT_METHOD_OPTIMIZATION };
}

export function tagCartConfidenceUx(): { ux_initiative_id: UxInitiativeId } {
  return { ux_initiative_id: UX_INITIATIVE_IDS.CART_CONFIDENCE };
}

export function getInitiativeById(id: string): UxInitiativeRegistryEntry | undefined {
  return UX_INITIATIVE_REGISTRY.find((e) => e.id === id);
}
